import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGlobalContext } from '../../../../Context/GlobalContext';
import { getCheckinReportList, createCheckinReportDownload, getCheckinReportDetail, deleteGuestCheckin } from '../../../../APICalling/APICalling';
import Loader from '../../../../Components/Loader/Loader';
import { toast } from 'react-toastify';
import Echo from '../../../../Components/Sockect/Echo';
import ReportHeader from '../ReportHeader/ReportHeader';
import DeleteIcon from '../../../../Assets/images/delete.svg';
import DeleteConfirmPopup from '../../../../Components/Popups/DeleteConfirmPopup/DeleteConfirmPopup';
import Placeholder from '../../../../Components/Placeholder/Placeholder';
import SearchNotFoundIcon from '../../../../Components/Icons/SearchNotFoundIcon';
import styles from './CheckinReport.module.css';
import { getPeriodText, calculateReportDateRange, isReportRangeWithinMonths } from '../../../../Utils/ReportDateUtils';
import { clearSharedUserChannelEvent } from '../../../../Utils/clearSharedUserChannelEvent';
import { downloadFileWithAuth, getDownloadFilename } from '../../../../Utils/downloadFileWithAuth';

const CheckinReport = ({ filterParams }) => {
	const navigate = useNavigate();
	const { selectedOutlet, userAssignedOutlets } = useGlobalContext();
	const [checkinData, setCheckinData] = useState([]);
	const [loading, setLoading] = useState(false);
	const [loadingMore, setLoadingMore] = useState(false);
	const [downloadLoading, setDownloadLoading] = useState(false);
	const [expandedBooking, setExpandedBooking] = useState(null);
	const [bookingDetails, setBookingDetails] = useState({});
	const [loadingDetails, setLoadingDetails] = useState({});
	const [deletingIds, setDeletingIds] = useState(new Set());
	const [deleteConfirmPopup, setDeleteConfirmPopup] = useState(null);
	const [outletName, setOutletName] = useState('');
	const [periodText, setPeriodText] = useState('');
	const [currentPage, setCurrentPage] = useState(1);
	const [hasMore, setHasMore] = useState(true);
	const [pagination, setPagination] = useState(null);
	const isLoadingRef = useRef(false);
	const isLoadingMoreRef = useRef(false);
	const observer = useRef();
	const fetchCheckinReportRef = useRef();
	const hasMoreRef = useRef(true);
	const currentPageRef = useRef(1);
	const paginationRef = useRef(null);

	// Get outlet name
	useEffect(() => {
		if (selectedOutlet && userAssignedOutlets?.length) {
			const outlet = userAssignedOutlets.find(o => o.id === selectedOutlet);
			setOutletName(outlet?.name || 'Unknown');
		}
	}, [selectedOutlet, userAssignedOutlets]);

	const fetchCheckinReport = useCallback(async (page = 1, isInitialLoad = false) => {
		if (isInitialLoad) {
			if (isLoadingRef.current) {
				return;
			}
		} else {
			if (isLoadingMoreRef.current || isLoadingRef.current) {
				return;
			}
		}

		try {
			if (isInitialLoad) {
				isLoadingRef.current = true;
				setLoading(true);
			} else {
				isLoadingMoreRef.current = true;
				setLoadingMore(true);
			}

			// Calculate dates based on filter using utility
			const { startDate: startDateTime, endDate: endDateTime } = calculateReportDateRange(filterParams);

			const params = {
				pagination: 1,
				page: page,
				daterange: `${startDateTime} / ${endDateTime}`,
				outlet: filterParams.outlet || selectedOutlet,
				archive: 1
			};

			// Add therapist filter
			if (filterParams?.staff !== undefined) {
				if (Array.isArray(filterParams.staff)) {
					params.therapist = filterParams.staff.join(',');
				} else {
					params.therapist = String(filterParams.staff || '').trim();
				}
			}
			if (filterParams.archive === 1) {
				params.archive = 1;
			}

			if (isInitialLoad) {
				setPeriodText(getPeriodText(filterParams));
			}

			const response = await getCheckinReportList(params);

			if (response?.data?.success) {
				const dataList = response.data?.data?.list || [];
				const paginationInfo = response.data?.data?.pagination;

				if (isInitialLoad) {
					setCheckinData(dataList);
				} else {
					setCheckinData(prev => [...prev, ...dataList]);
				}

				if (paginationInfo) {
					setPagination(paginationInfo);
					paginationRef.current = paginationInfo;
					setCurrentPage(page);
					currentPageRef.current = page;
					
					// Check if there are more pages - API returns camelCase (currentPage, lastPage)
					if (paginationInfo.currentPage >= paginationInfo.lastPage) {
						setHasMore(false);
						hasMoreRef.current = false;
					} else {
						setHasMore(true);
						hasMoreRef.current = true;
					}
				}
			} else {
				if (isInitialLoad) {
					toast.error(response?.response?.data?.message || response?.data?.message || response?.message || 'Failed to fetch check-in report');
					setCheckinData([]);
				}
			}
		} catch (error) {
			console.error('Error fetching check-in report:', error);
			if (isInitialLoad) {
				toast.error(error?.response?.data?.message || error?.message || 'An error occurred while fetching check-in report');
				setCheckinData([]);
			}
		} finally {
			isLoadingRef.current = false;
			isLoadingMoreRef.current = false;
			setLoading(false);
			setLoadingMore(false);
		}
	}, [filterParams, selectedOutlet]);

	// Store fetchCheckinReport in ref for use in observer
	useEffect(() => {
		fetchCheckinReportRef.current = fetchCheckinReport;
	}, [fetchCheckinReport]);

	// Initial load when filterParams change
	// Only call API if filterParams.outlet matches selectedOutlet (i.e., Generate was clicked)
	useEffect(() => {
		if (filterParams && filterParams.date && filterParams.outlet === selectedOutlet) {
			setCurrentPage(1);
			currentPageRef.current = 1;
			setCheckinData([]);
			setExpandedBooking(null);
			setHasMore(true);
			hasMoreRef.current = true;
			paginationRef.current = null;
			fetchCheckinReport(1, true);
		}
	}, [filterParams, fetchCheckinReport, selectedOutlet]);

	// Intersection Observer for infinite scroll
	const lastItemRef = useCallback(node => {
		if (loading || loadingMore) return;
		if (observer.current) observer.current.disconnect();
		observer.current = new IntersectionObserver(entries => {
			if (entries[0].isIntersecting && hasMore && pagination) {
				const nextPage = currentPage + 1;
				// API returns camelCase (lastPage)
				if (nextPage <= pagination.lastPage && fetchCheckinReportRef.current) {
					setCurrentPage(nextPage);
					fetchCheckinReportRef.current(nextPage, false);
				}
			}
		}, {
			root: null,
			rootMargin: '100px',
			threshold: 0.1
		});
		if (node) observer.current.observe(node);
	}, [loading, loadingMore, hasMore, pagination, currentPage]);

	// Cleanup observer on unmount
	useEffect(() => {
		return () => {
			if (observer.current) {
				observer.current.disconnect();
			}
		};
	}, []);

	const handleBookingClick = async (booking) => {
		const bookingId = booking.booking_id;

		// Don't expand if there's no booking_id (Unlinked)
		if (!bookingId) {
			return;
		}

		// Toggle if already expanded
		if (expandedBooking === bookingId) {
			setExpandedBooking(null);
			return;
		}

		// Expand this booking
		setExpandedBooking(bookingId);

		// If we already have the data, don't fetch again
		if (bookingDetails[bookingId]) {
			return;
		}

		// Fetch booking details
		try {
			setLoadingDetails(prev => ({ ...prev, [bookingId]: true }));

			const response = await getCheckinReportDetail(bookingId);

			if (response?.data?.success) {
				setBookingDetails(prev => ({
					...prev,
					[bookingId]: response.data.data
				}));
			} else {
				toast.error('Failed to fetch booking details');
			}
		} catch (error) {
			console.error('Error fetching booking details:', error);
			toast.error('Failed to fetch booking details');
		} finally {
			setLoadingDetails(prev => ({ ...prev, [bookingId]: false }));
		}
	};

	const formatDateToYMD = (dateValue) => {
		if (!dateValue) return '';
		const date = new Date(dateValue);
		if (Number.isNaN(date.getTime())) return '';
		const year = date.getFullYear();
		const month = String(date.getMonth() + 1).padStart(2, '0');
		const day = String(date.getDate()).padStart(2, '0');
		return `${year}-${month}-${day}`;
	};

	const getBookingServiceDate = (booking) => {
		const parseDisplayDateTime = (raw) => {
			if (!raw) return '';
			const datePart = String(raw).split('|')?.[0]?.trim();
			const cleanedDatePart = datePart.includes(',')
				? datePart.split(',').slice(1).join(',').trim()
				: datePart;
			return formatDateToYMD(cleanedDatePart);
		};

		// Prefer service date/time from check-in detail API (not booking created date)
		const fromServiceDateTime = parseDisplayDateTime(booking?.service_date_time);
		if (fromServiceDateTime) {
			return fromServiceDateTime;
		}

		const serviceDate = booking?.services?.[0]?.service_at?.split(' ')?.[0];
		if (serviceDate) {
			return serviceDate;
		}

		const date = booking?.date?.split?.(' ')?.[0];
		if (date) {
			return date;
		}

		return parseDisplayDateTime(booking?.booking_date);
	};

	const resolveBookingDetails = async (bookingId) => {
		if (!bookingId) {
			return null;
		}

		if (bookingDetails[bookingId]) {
			return bookingDetails[bookingId];
		}

		try {
			setLoadingDetails(prev => ({ ...prev, [bookingId]: true }));
			const response = await getCheckinReportDetail(bookingId);
			if (response?.data?.success) {
				const details = response.data.data;
				setBookingDetails(prev => ({
					...prev,
					[bookingId]: details
				}));
				return details;
			}
		} catch (error) {
			console.error('Error fetching booking details:', error);
		} finally {
			setLoadingDetails(prev => ({ ...prev, [bookingId]: false }));
		}

		return null;
	};

	const handleBookingIdClick = async (e, booking) => {
		e.stopPropagation();
		const bookingId = booking?.booking_id;
		if (!bookingId) return;

		const details = await resolveBookingDetails(bookingId);
		const serviceDate = getBookingServiceDate(details || booking);

		sessionStorage.setItem("skipCurrentTimeAutoScroll", "1");
		navigate(`/?bookingId=${bookingId}&date=${serviceDate}`);
	};

	const handleDownloadCSV = async (bookingId) => {
		// TODO: Implement individual booking CSV download
		toast.info('CSV download for individual booking coming soon');
	};

	const handleDeleteGuestCheckin = (e, guestCheckinId, bookingId, guestName) => {
		e.stopPropagation(); // Prevent row click event

		if (!guestCheckinId) {
			toast.error('Guest check-in ID not found');
			return;
		}

		// Show delete confirmation popup
		setDeleteConfirmPopup({
			guestCheckinId,
			bookingId,
			guestName: guestName || 'this guest'
		});
	};

	const handleConfirmDelete = async () => {
		if (!deleteConfirmPopup) return;

		const { guestCheckinId, bookingId } = deleteConfirmPopup;

		try {
			setDeletingIds(prev => new Set([...prev, guestCheckinId]));

			const response = await deleteGuestCheckin(guestCheckinId);

			if (response?.data?.success || response?.success) {
				toast.success('Guest check-in deleted successfully');
				
				// Remove the guest from the checkinData
				setCheckinData(prev => prev.map(booking => {
					if (booking.booking_id === bookingId || !booking.booking_id) {
						const updatedGuests = (booking.guests || []).filter(
							guest => guest.id !== guestCheckinId && guest.guest_checkin_id !== guestCheckinId
						);
						
						// If no guests left, remove the entire booking (for both linked and unlinked)
						if (updatedGuests.length === 0) {
							return null;
						}
						
						return { ...booking, guests: updatedGuests };
					}
					return booking;
				}).filter(Boolean)); // Remove null entries
				
				// Close popup
				setDeleteConfirmPopup(null);
			} else {
				const errorMessage = response?.data?.message || response?.message || 'Failed to delete guest check-in';
				toast.error(errorMessage);
			}
		} catch (error) {
			console.error('Error deleting guest check-in:', error);
			const errorMessage = error?.response?.data?.message || error?.message || 'An error occurred while deleting guest check-in';
			toast.error(errorMessage);
		} finally {
			setDeletingIds(prev => {
				const next = new Set(prev);
				next.delete(guestCheckinId);
				return next;
			});
		}
	};

	const handleCancelDelete = () => {
		setDeleteConfirmPopup(null);
	};

	const handleDownloadAll = async () => {
		if (!checkinData || checkinData.length === 0) {
			toast.warning('No data available to download');
			return;
		}
		if (!isReportRangeWithinMonths(filterParams, 3)) {
			toast.error('Please select a date range within 90 days to proceed.');
			return;
		}

		// Get user_id and token from localStorage
		const userId = localStorage.getItem('user_id');
		const token = localStorage.getItem('token');
		
		if (!userId) {
			toast.error('User ID not found');
			return;
		}

		if (!token) {
			toast.error('Authentication token not found');
			return;
		}

		// Create user-specific private channel name
		const channelName = `export-checkins.${userId}`;
		let channel;
		let handleEvent = null;
		let toastId = null;

		setDownloadLoading(true);
		
		// Show loading toast
		toastId = toast.loading("Downloading in progress...");
		
		try {
			// Join export channel — stopListening only (do not Echo.leave).
			channel = Echo.private(channelName);
			const eventName = 'export.status.updated';
			let handled = false;

			handleEvent = async (event) => {
				try {
					const status = event?.status || event?.data?.status;

					if (status === 'processing' || status === 'queued') {
						return;
					}

					if (handled) return;
					handled = true;

					if (status === 'failed' || status === 'error') {
						const errorMessage = event?.message || event?.data?.message || 'Export failed';
						toast.error(errorMessage);
						cleanup();
						setDownloadLoading(false);
						return;
					}

					const downloadUrl = event?.data?.download_url || 
									  event?.download_url ||
									  event?.data?.data?.download_url;

					if (!downloadUrl) {
						toast.error('Download URL not received. Please try again.');
						cleanup();
						setDownloadLoading(false);
						return;
					}

					try {
						await downloadFileWithAuth(
							downloadUrl,
							getDownloadFilename(event, downloadUrl, 'checkin-report.csv')
						);
						toast.success('Downloaded successfully');
					} catch (downloadError) {
						toast.error('Failed to start download');
					}

					cleanup();
					setDownloadLoading(false);
				} catch (error) {
					handled = true;
					toast.error('Failed to process download');
					cleanup();
					setDownloadLoading(false);
				}
			};

			const cleanup = () => {
				clearSharedUserChannelEvent(channelName, eventName, channel, handleEvent);
				if (toastId) {
					toast.dismiss(toastId);
					toastId = null;
				}
			};

			clearSharedUserChannelEvent(channelName, eventName, channel);

			try {
				channel.listen(`.${eventName}`, handleEvent);
			} catch (e) {}

			try {
				const pusherChannel = Echo?.connector?.pusher?.channel?.(`private-${channelName}`);
				pusherChannel?.bind?.(eventName, handleEvent);
			} catch (e) {}

			// Error handler
			if (typeof channel.error === 'function') {
				channel.error((error) => {
					toast.error('Socket connection error');
					cleanup();
					setDownloadLoading(false);
				});
			}

			// Wait for subscription to be ready before calling API
			await new Promise((resolve) => setTimeout(resolve, 500));

			// Calculate dates for export using utility
			const { startDate: downloadStartDateTime, endDate: downloadEndDateTime } = calculateReportDateRange(filterParams);

			// Call create API to trigger report generation
			const downloadParams = {
				daterange: `${downloadStartDateTime} / ${downloadEndDateTime}`,
				outlet: filterParams.outlet || selectedOutlet
			};

			// Add therapist filter if provided
			if (filterParams?.staff !== undefined) {
				if (Array.isArray(filterParams.staff)) {
					downloadParams.therapist = filterParams.staff.join(',');
				} else {
					downloadParams.therapist = String(filterParams.staff || '').trim();
				}
			}
			if (filterParams.archive === 1) {
				downloadParams.archive = 1;
			}

			const createResponse = await createCheckinReportDownload(downloadParams);

			// Check for success in the response
			const isSuccess = createResponse?.data?.data?.success || createResponse?.data?.success;
			
			if (isSuccess) {
				return; // Wait for socket event
			}

			// If not successful, handle error
			if (!isSuccess) {
				const backendError = createResponse?.data?.data?.message ||
								   createResponse?.data?.message || 
								   createResponse?.message || 
								   'Failed to initiate download';
				
				// Show user-friendly error message
				let userMessage = 'Failed to generate report. ';
				
				if (backendError.includes('S3') || backendError.includes('AWS') || backendError.includes('storage')) {
					userMessage += 'Server storage configuration issue. Please contact support.';
				} else if (backendError.includes('ACL')) {
					userMessage += 'File permission error. Please contact support.';
				} else {
					userMessage = backendError.substring(0, 200);
				}
				
				toast.error(userMessage, {
					autoClose: 6000
				});
				
				cleanup();
				setDownloadLoading(false);
				return;
			}

		} catch (error) {
			const errorMessage = error?.response?.data?.message || 
							   error?.message || 
							   'An error occurred while preparing download';
			
			toast.error(errorMessage, {
				autoClose: 5000
			});
			
			if (toastId) toast.dismiss(toastId);
			setDownloadLoading(false);
			
			try {
				clearSharedUserChannelEvent(channelName, 'export.status.updated', channel, handleEvent);
			} catch (e) {}
		}
	};



	return (
		<div className={styles.checkinContainer}>
			{/* Header Section */}
			<div className={styles.reportHeaderWrapper}>
				<ReportHeader
					outletName={outletName}
					periodText={periodText}
					onDownload={handleDownloadAll}
					downloadLoading={downloadLoading}
					hasData={checkinData && checkinData.length > 0}
					downloadButtonText="Download CSV"
				/>
			</div>

			{/* Table Section */}
			{loading ? (
				<div className={styles.loaderContainer}>
					<Loader />
				</div>
			) : checkinData && checkinData.length > 0 ? (
				<div className={styles.tableWrapper}>
					<table className={styles.checkinTable}>
						<thead style={{ position: 'sticky', top: 0, zIndex: 10, }}>
							<tr>
								<th className={styles.colBookingId}>Booking Id</th>
								<th className={styles.colCustomerName}>Customer Name</th>
								<th className={styles.colCountry}>Nationality </th>
								<th className={styles.colContact}>Contact</th>
								<th className={styles.colPassport}>Passport Number</th>
								<th className={styles.colCheckinTime}>Check-in Time</th>
								<th className={styles.colAction}>Action</th>
							</tr>
						</thead>
						<tbody>
							{checkinData
								.filter(booking => (booking.guests || []).length > 0) // Filter out bookings with no guests
								.map((booking, index) => {
							const bookingId = booking.booking_id;
							const isExpanded = expandedBooking === bookingId;
								const guests = booking.guests || [];
								const hasBookingId = !!bookingId;
								const rowClassName = hasBookingId ? styles.linkedRow : '';

								return (
									<React.Fragment key={bookingId || `unlinked-${index}`}>
										{guests.map((guest, guestIndex) => {
											const guestCheckinId = guest.id || guest.guest_checkin_id;
											const isDeleting = deletingIds.has(guestCheckinId);
											
											return (
												<tr
													key={`${bookingId || 'unlinked'}-${guestIndex}`}
													className={`${styles.bookingRow} ${rowClassName} ${isExpanded ? styles.expanded : ''} ${!hasBookingId ? styles.unlinkedRow : ''}`}
													onClick={() => hasBookingId && handleBookingClick(booking)}
												>
													{guestIndex === 0 && (
														<td 
															className={styles.colBookingId}
															rowSpan={guests.length}
														>
															{hasBookingId ? (
																<span 
																	className={styles.bookingIdLink}
																	onClick={(e) => handleBookingIdClick(e, booking)}
																>
																	{`#${bookingId}`}
																</span>
															) : (
																<span>Unlinked</span>
															)}
														</td>
													)}
													<td className={styles.colCustomerName}>
														{guest.customer_name || 'N/A'}
													</td>
													<td className={styles.colCountry}>
														{guest.country || 'N/A'}
													</td>
													<td className={styles.colContact}>
														{guest.contact || 'N/A'}
													</td>
													<td className={styles.colPassport}>
														<span className={styles.maskedText}>
															{guest.passport_number || 'N/A'}
														</span>
													</td>
													<td className={styles.colCheckinTime}>
														{guest.checkin_time || '-'}
													</td>
													<td className={styles.colAction}>
														<button
															className={styles.deleteButton}
															onClick={(e) => handleDeleteGuestCheckin(e, guestCheckinId, bookingId, guest.customer_name)}
															disabled={isDeleting}
															title="Delete"
														>
															{isDeleting ? (
																<span className={styles.deletingText}>...</span>
															) : (
																<img src={DeleteIcon} alt="Delete" />
															)}
														</button>
													</td>
												</tr>
											);
										})}
										{/* Expanded booking details - only show for linked bookings */}
										{isExpanded && hasBookingId && (
											<tr className={styles.expandedRow}>
												<td colSpan="7">
													<div className={styles.bookingDetailsContainer}>
														{loadingDetails[bookingId] ? (
															<div className={styles.loaderSmall}>
																<Loader />
															</div>
														) : bookingDetails[bookingId] ? (
															<>
																{/* Booking Info */}
																<div className={styles.bookingInfo}>
																	<div className={styles.bookingInfoItem}>
																		<span className={styles.bookingInfoLabel}>Customer Name:</span>
																		<span className={styles.bookingInfoValue}>
																			{bookingDetails[bookingId].customer_name || 'N/A'}
																		</span>
																	</div>
																	<div className={styles.bookingInfoItem}>
																		<span className={styles.bookingInfoLabel}>Booking Date:</span>
																		<span className={styles.bookingInfoValue}>
																			{bookingDetails[bookingId].booking_date || 'N/A'}
																		</span>
																	</div>
																	<div className={styles.bookingInfoItem}>
																		<span className={styles.bookingInfoLabel}>Status:</span>
																		<span className={styles.bookingInfoValue}>
																			{bookingDetails[bookingId].status || 'N/A'}
																		</span>
																	</div>
																</div>

																{/* Services Table */}
																{bookingDetails[bookingId].services && bookingDetails[bookingId].services.length > 0 ? (
																	<div className={styles.servicesSection}>
																		<table className={styles.servicesTable}>
																			<thead>
																				<tr>
																					<th className={styles.colServiceName}>Service name</th>
																					<th className={styles.colTherapistName}>Therapist name</th>
																				</tr>
																			</thead>
																			<tbody>
																				{bookingDetails[bookingId].services.map((service, serviceIndex) => (
																					<tr key={`service-${serviceIndex}`}>
																						<td className={styles.colServiceName}>
																							{service.service_name || 'N/A'}
																						</td>
																						<td className={styles.colTherapistName}>
																							{service.therapist || 'N/A'}
																						</td>
																					</tr>
																				))}
																			</tbody>
																		</table>
																	</div>
																) : (
																	<p className={styles.noData}>No services available</p>
																)}
															</>
														) : (
															<p className={styles.noData}>No booking details available</p>
														)}
													</div>
												</td>
											</tr>
										)}
									</React.Fragment>
								);
							})}
							{/* Sentinel element for infinite scroll - only show if hasMore and not loading */}
							{hasMore && !loadingMore && (
								<tr>
									<td colSpan="7" ref={lastItemRef} style={{ height: '1px', padding: 0 }}></td>
								</tr>
							)}
						</tbody>
					</table>
				</div>
			) : (
				<div className={styles.noData}>
					<Placeholder
						icon={<SearchNotFoundIcon width="24" height="24" color="white"/>}
						heading="No check-in data found"
						para="Please try changing the filters"
					/>
				</div>
			)}

			{/* Delete Confirmation Popup */}
			{deleteConfirmPopup && (
				<DeleteConfirmPopup
					title="Delete Check-in"
					deleteGuestName={deleteConfirmPopup.guestName}
					onCancel={handleCancelDelete}
					onConfirm={handleConfirmDelete}
					isDeleting={deletingIds.has(deleteConfirmPopup.guestCheckinId)}
					from="checkin-report"
				/>
			)}
		</div>
	);
};

export default CheckinReport;
